import { Divider, Grid2, TextField, Typography } from "@mui/material";
import { Control, Controller, useWatch } from "react-hook-form";

import { CheckboxTextField } from "@/controls/CheckboxTextField";
import { PRODUCT_AREA_ASSET_ID, Product } from "@/shared/models/Product";
import { CustomerSelector } from "@/shared/selectors/CustomerSelector";
import { parseNumberInput } from "@/shared/utils/parseNumberInput";

type Props = {
	control: Control<Product>;
	disabled: boolean;
	areaAssetId: number;
};

export const FinishSpecsFields = ({
	control,
	disabled,
	areaAssetId,
}: Props) => {
	const isSaw = areaAssetId === PRODUCT_AREA_ASSET_ID.saw;
	const isWax = areaAssetId === PRODUCT_AREA_ASSET_ID.wax;
	const isGalvSawed = areaAssetId === PRODUCT_AREA_ASSET_ID["galv-sawed"];

	// Wax has no Banded toggle; treat its band-dependent fields as always enabled.
	const banded = useWatch({ control, name: "Banded" });
	const bandFieldsDisabled = disabled || (!isWax && banded === false);

	return (
		<>
			<Grid2 size={{ xs: 12 }}>
				<Typography
					variant="subtitle2"
					fontWeight={600}
					color="primary"
					sx={{ mb: 0.5, mt: 1 }}
				>
					Finish Specs
				</Typography>
				<Divider />
			</Grid2>

			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="CustomerId"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<CustomerSelector
							label="Customer"
							value={field.value}
							onChange={field.onChange}
							fieldError={error}
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>

			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="CustomerPartNumber"
					control={control}
					render={({ field }) => (
						<TextField
							label={
								isSaw || isGalvSawed ? "Customer Part #" : "Beam Cust. Part #"
							}
							value={field.value || ""}
							onChange={field.onChange}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>

			{!isWax && (
				<Grid2 size={{ xs: 6, md: 2 }}>
					<Controller
						name="Banded"
						control={control}
						render={({ field }) => (
							<CheckboxTextField
								label="Banded"
								checked={!!field.value}
								onChange={(checked) => field.onChange(checked)}
								disabled={disabled}
							/>
						)}
					/>
				</Grid2>
			)}

			<Grid2 size={{ xs: 6, md: 2 }}>
				<Controller
					name="BandLength"
					control={control}
					render={({ field }) => (
						<TextField
							label="Band Length"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(parseNumberInput(e.target.value, { min: 0 }))
							}
							onBlur={() => {
								field.onBlur();
								if (typeof field.value === "number") {
									const r = Math.round(field.value * 1000) / 1000;
									if (r !== field.value) field.onChange(r);
								}
							}}
							fullWidth
							size="small"
							disabled={bandFieldsDisabled}
							inputProps={{ min: 0, step: 0.001 }}
						/>
					)}
				/>
			</Grid2>

			{(isSaw || isGalvSawed) && (
				<Grid2 size={{ xs: 6, md: 2 }}>
					<Controller
						name="NumberOfBands"
						control={control}
						render={({ field }) => (
							<TextField
								label="# of Bands"
								type="number"
								value={field.value ?? ""}
								onChange={(e) =>
									field.onChange(
										parseNumberInput(e.target.value, { integer: true, min: 1 }),
									)
								}
								fullWidth
								size="small"
								disabled={bandFieldsDisabled}
								inputProps={{ min: 1, step: 1 }}
							/>
						)}
					/>
				</Grid2>
			)}

			{!isSaw && (
				<Grid2 size={{ xs: 6, md: 2 }}>
					<Controller
						name="GroovedTimber"
						control={control}
						render={({ field }) => (
							<TextField
								label="Grooved Timber"
								value={field.value || ""}
								onChange={field.onChange}
								fullWidth
								size="small"
								disabled={disabled}
							/>
						)}
					/>
				</Grid2>
			)}

			{isWax && (
				<Grid2 size={{ xs: 6, md: 2 }}>
					<Controller
						name="Cardboard"
						control={control}
						render={({ field }) => (
							<CheckboxTextField
								label="Cardboard"
								checked={!!field.value}
								onChange={(checked) => field.onChange(checked)}
								disabled={disabled}
							/>
						)}
					/>
				</Grid2>
			)}
		</>
	);
};
